import { Dialog } from "@omnia/shared/shell/dialog"
import { defineDirective, IOmniaShell, isNumber} from "../../../omnia"
import { CroppingGroupService } from "../../../services/grouping"
import { NutrientFilter } from "../../../services/nutrientFilter"
import { IFieldsService, INtrReq, INutrient, INutritionService, NutrientType } from "../../../types"
import { LimeEnabledFromYear } from "../../../utils"
import { getNutrientDefs } from "../nutrient-def"
import { CroppingVm } from "./cropping-vm"
import { NutrientViewModel } from "./nutrient-vm"
import { ConfigGroup, IBatchEditParameters, IBatchEditResults } from "./types"
import { escape } from "@omnia/shared/utils/regexp"


export class BatchEditDlg extends Dialog {

    static create() {
        return defineDirective(BatchEditDlg, "app/nutrition/requirements/edit/batch/batch.html");
    }


    private cropping: CroppingVm[];
    public groups: ConfigGroup[];

    private _nutrients: NutrientFilter;

    public editNutrients: NutrientViewModel[];

    private _textFilter: string;
    private _textFilterRegExp: RegExp;

    private _precisionFilter: boolean = false;

    public comments: string
    public editComments: boolean = false

    public targetpH: number
    public editTargetpH = false

    static $inject = [
        "$scope",
        "OmniaNutritionService",
        "OmniaFieldsService",
        "OmniaNutritionCroppingGroup",
        "OmniaShell"];

    constructor(
        $scope: ng.IScope,
        private svc: INutritionService,
        private fieldSvc: IFieldsService,
        private groupingSvc: CroppingGroupService,
        shell: IOmniaShell) {

        super($scope, shell);

    }

    $onInit() {

        let session = this.shell.getSession();
        let enableLime = session.yearId >= LimeEnabledFromYear;

        this._nutrients = new NutrientFilter(session, this.svc.settings);

        this.setupEditNutrients(enableLime);
        this.setupCropping();

        let parameters = <IBatchEditParameters>this.arguments;
        if (parameters) {
            this.comments = parameters.comments;
        }


    }

    private setupEditNutrients(enableLime: boolean) {

        let req: INtrReq = <any>{};
        let parameters = <IBatchEditParameters>this.arguments;

        if (parameters) {
            req = <any>(parameters.nutrients || {});
        }

        this.editNutrients = getNutrientDefs()
            .filter(n => enableLime || (n.nutrientType != NutrientType.Lime))
            .map(def => new NutrientViewModel(def, req));

    }

    private setupCropping() {

        const crops = this.fieldSvc.cropping.filter(c => c.currentYear && c.authorized);

        this.cropping = CroppingVm.create(this.svc, crops);

        this.applyFilters();

        this.updateGroups();


    }

    get groupings() {
        return this.groupingSvc.groupings;
    }

    get currentGrouping() {
        return this.groupingSvc.currentGrouping;
    }

    set currentGrouping(value) {
        this.groupingSvc.currentGrouping = value;
        this.updateGroups();
    }

    get nutrients(): INutrient[] {
        return this._nutrients.nutrients;
    }

    private updateGroups() {
        let grouping = this.currentGrouping || this.groupingSvc.groupings[0];
        this.groups = grouping.grouping(this.cropping);
        this.applyGroupFilters();
    }

    toggleGroupSelection(group: ConfigGroup) {
        let cropping = group.cropping.filter(c => !c.excluded);
        let select = cropping.some(c => !c.selected);
        cropping.forEach(c => c.selected = select);
    }

    hasUnselectedCropping(group: ConfigGroup) {
        return group && group.cropping.some(c => !c.selected)
    }

    get textFilter(): string {
        return this._textFilter;
    }

    set textFilter(value: string) {
        this._textFilter = value;
        this._textFilterRegExp = this._textFilter ? new RegExp(escape(this._textFilter), "i") : null;
        this.applyFilters();
    }

    get precisionFilter(): boolean {
        return this._precisionFilter;
    }

    set precisionFilter(value: boolean) {
        this._precisionFilter = value;
        this.applyFilters();
    }

    private updateOnNutrientChanged(ntr: NutrientViewModel) {
        this.applyFilters();
    }

    private applyFilters() {

        let nutrientsMask = this.getEditedNutrientsMask();

        this.cropping.forEach(c => {

            let excluded = false;

            if (this._textFilterRegExp) {
                excluded = this._textFilterRegExp.test(c.croppingVm.description);
            }

            if (nutrientsMask && (!this._precisionFilter)) {

                if (c.multipleZonesNutrientMask & nutrientsMask) {
                    excluded = true;
                    c.selected = false;
                }
            }

            c.excluded = excluded;

        });

        this.applyGroupFilters();

    }

    private applyGroupFilters() {
        if (this.groups) {
            this.groups.forEach(g => g.visible = g.cropping.some(c => c.visible));
        }
    }

    getEditedNutrientsMask(): number {

        if (!this.editNutrients) return 0;

        let nutrientsMask = this.editNutrients.reduce((mask, ntr) => {
            if (ntr.selected && isNumber(ntr.value)) {
                mask |= Math.pow(2, ntr.nutrientType - 1);
            }
            return mask;
        }, 0);

        return nutrientsMask;
    }

    getTargets(): CroppingVm[] {
        return this.cropping.filter(c => c.selected && (!c.excluded));
    }

    getResults(): IBatchEditResults {

        const targets = this.getTargets()

        const results: IBatchEditResults = {
            croppingIds: targets.map(t => t.croppingId),
            comments: this.editComments ? (this.comments || "") : undefined,
            targetpH: this.editTargetpH ? this.targetpH : undefined,
            nutrients: {}
        }

        results.nutrients = this.editNutrients.filter(n => n.selected).reduce((nutrients, n) => {
            const v = n.value
            if (isNumber(v)) {
                nutrients["Required" + n.key] = v
            }
            return nutrients
        }, {})

        return results

    }

    canCommit(): boolean {

        let targets = this.getTargets();
        if (!targets.length) return false;

        let nutrients = this.getEditedNutrientsMask();

        if (!(nutrients || this.editComments || this.editTargetpH)) return false;

        if (this.editTargetpH && (!this.targetpH)) {
            return false
        }

        return this.form && this.form.$valid;

    }

    commit() {

        if (!this.canCommit()) return;

        let result = this.getResults();

        this.close(result);

    }
}
